import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  TablePagination,
  Container,
  Grid,
  Card,
  CardContent,
  Chip,
  Fade,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Avatar,
  useTheme,
  alpha,
  Checkbox
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  AssignmentTurnedIn as IssueIcon,
  Comment as CommentIcon,
  DeleteSweep as DeleteSweepIcon,
  PauseCircle as StopIcon,
  PlayCircle as ActivateIcon,
  LowPriority as PriorityIcon,
  ContentCopy as CopyIcon,
  LocationSearching as RecognizeCoordsIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from '../utils/axios';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';

const TYPES = {
  'стелс': 'Стелс',
  'тайник': 'Тайник',
  'прикоп': 'Прикоп',
  'Прикоп-тайник':'Прикоп-тайник',
  'снежный прикоп': 'Снежный прикоп',
  'земляной прикоп': 'Земляной прикоп',
  'магнит': 'Магнит'
};

const STATUS = {
  active: 'Активный',
  inactive: 'Неактивный',
  issued: 'Выдан',
  stopped: 'Остановлен'
};

const COORD_STATUS_LABEL = {
  success: 'Найдены',
  processing: 'Обработка',
  not_found: 'Не найдены',
  error: 'Ошибка'
};

const getCoordStatusColor = (status) => {
  if (status === 'success') return 'success';
  if (status === 'processing') return 'info';
  if (status === 'error') return 'error';
  if (status === 'not_found') return 'warning';
  return 'default';
};

const formatCoordinateValue = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  return value.toFixed(6);
};

const formatUsd = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return amount.toFixed(2);
};

const initialFormData = {
  cityId: '',
  districtId: '',
  productId: '',
  packagingId: '',
  courierId: '',
  type: '',
  status: 'active',
  photo: '',
  recognizeCoordinates: false,
};

const Addresses = () => {
  const { currentUser, refreshUser } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [products, setProducts] = useState([]);
  const [packagings, setPackagings] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [formError, setFormError] = useState({});
  const [addressCount, setAddressCount] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  
  useEffect(() => {
    const photos = formData.photo.split('\n').filter(p => p);
    setAddressCount(photos.length);
  }, [formData.photo]);
  // Состояния для пагинации
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Состояния для фильтров
  const [filters, setFilters] = useState({
    cityId: '',
    districtId: '',
    type: '',
    status: 'active',
    courierId: '',
    packagingId: '',
    photo: '',
    id: '',
  });

  // Состояния для выдачи адреса
  const [issueDialog, setIssueDialog] = useState(false);
  const [issueComment, setIssueComment] = useState('');
  const [issuingAddress, setIssuingAddress] = useState(null);
  
  // Состояние для просмотра комментариев
  const [commentDialog, setCommentDialog] = useState(false);
  const [viewingAddress, setViewingAddress] = useState(null);
  
  // Состояние для массового удаления
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  
  // Состояние для установки приоритета
  const [priorityDialog, setPriorityDialog] = useState(false);
  const [priorityValue, setPriorityValue] = useState(0);
  const [recognizingAddressId, setRecognizingAddressId] = useState(null);
  const [ocrBalanceLoading, setOcrBalanceLoading] = useState(false);
  const [ocrBalance, setOcrBalance] = useState(null);
  const formatDate = (date) => {
      if (!date) return '-';
      return format(new Date(date), 'dd.MM.yyyy HH:mm');
  };

  // Проверка лимитов для текущего пользователя (оператора).
  // Куратор не ограничен. Данные приходят с getMe в поле addressActions.
  const isCurator = currentUser?.role === 'curator';
  const canViewAddresses = isCurator || currentUser?.addressActions?.canView !== false;
  const canEditAddresses = isCurator || currentUser?.addressActions?.canEdit !== false;
  const canDeleteAddresses = isCurator || currentUser?.addressActions?.canDelete !== false;
  const viewRemaining = isCurator
    ? null
    : (currentUser?.addressActions?.viewLimit ?? null);
  const editRemaining = isCurator
    ? null
    : (currentUser?.addressActions?.editLimit ?? null);
  const deleteRemaining = isCurator
    ? null
    : (currentUser?.addressActions?.deleteLimit ?? null);
  // Считаем оставшееся количество действий: осталось = лимит - использовано
  const viewLeft = (viewRemaining == null) ? null : Math.max(0, viewRemaining - (currentUser?.addressActions?.viewUsed || 0));
  const editLeft = (editRemaining == null) ? null : Math.max(0, editRemaining - (currentUser?.addressActions?.editUsed || 0));
  const deleteLeft = (deleteRemaining == null) ? null : Math.max(0, deleteRemaining - (currentUser?.addressActions?.deleteUsed || 0));
  // Загрузка данных
  const fetchData = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      // Формируем URL с параметрами пагинации и фильтрации
      const params = {
        page: pagination.page,
        limit: pagination.limit, 
        ...filters
      };
console.log('Отправляемые параметры:', filters);


      const queryParams = new URLSearchParams(params);
      console.log('Отправляемые параметры:', params);

      const endpoints = {
        addresses: `/addresses?${queryParams}`,
        cities: '/cities', 
        districts: '/districts',
        products: '/products',
        packagings: '/packagings',
        couriers: '/staff'
      };

      const responses = await Promise.all(
        Object.entries(endpoints).map(([key, endpoint]) => 
          axiosInstance.get(endpoint)
            .then(response => ({ key, data: response.data }))
            .catch(error => {
              console.error(`Error fetching ${key}:`, error);
              setFetchError(prev => 
                `${prev ? prev + '\n' : ''}Ошибка загрузки ${key}: ${error.message}`
              );
              return { key, data: { data: [] } };
            })
        )
      );

      responses.forEach(({ key, data }) => {
        switch(key) {
          case 'addresses': 
            console.log('Загруженные адреса:', data.data);
            console.log('Первый адрес с приоритетом:', data.data[0]?.priority);
            setAddresses(data.data);
            setPagination({
              page: data.pagination.page,
              limit: data.pagination.limit,
              total: data.pagination.total,
              pages: data.pagination.pages
            });
            break;
          case 'cities': 
            console.log('Загруженные города:', data.data);
            setCities(data.data); 
            break;
          case 'districts': 
            console.log('Загруженные районы:', data.data);
            setDistricts(data.data); 
            break;
          case 'products': setProducts(data.data); break;
          case 'packagings': setPackagings(data.data); break;
          case 'couriers': 
            // Фильтруем только курьеров
            const onlyCouriers = data.data.filter(staff => staff.role === 'courier');
            console.log('Загруженные курьеры:', onlyCouriers);
            setCouriers(onlyCouriers); 
            break;
          default: break;
        }
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      setFetchError('Ошибка загрузки данных: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenAiOcrBalance = useCallback(async () => {
    try {
      setOcrBalanceLoading(true);
      const response = await axiosInstance.get('/addresses/openai-ocr-balance');
      setOcrBalance(response?.data?.data || null);
    } catch (error) {
      setOcrBalance({
        ok: false,
        reason: error?.response?.data?.error || 'openai_balance_request_failed'
      });
    } finally {
      setOcrBalanceLoading(false);
    }
  }, []);

  const handleRefreshPageData = () => {
    fetchData();
    fetchOpenAiOcrBalance();
  };

  const handleRecognizeCoordinates = async (address) => {
    if (!address?._id) return;

    const isRetry = Boolean(address?.coordinateRecognition?.status);

    setRecognizingAddressId(address._id);
    try {
      const response = await axiosInstance.post(`/addresses/${address._id}/recognize-coordinates`, {
        retry: isRetry
      });
      const updatedAddress = response?.data?.data;
      const recognition = response?.data?.recognition;

      if (updatedAddress?._id) {
        setAddresses((prev) => prev.map((item) => (item._id === updatedAddress._id ? updatedAddress : item)));
      }

      if (recognition?.status === 'success') {
        enqueueSnackbar(`Координаты распознаны: ${recognition.latitude}, ${recognition.longitude}`, { variant: 'success' });
      } else if (recognition?.status === 'not_found') {
        const reasonMap = {
          no_url_found_in_photo_field: 'В поле фото не найдено ссылки или координат.',
          coordinates_not_detected: 'Координаты не найдены в тексте/изображении.',
          ocr_dependency_unavailable: 'OCR не установлен в контейнере (tesseract.js).',
          ocr_api_key_missing: 'Не задан API ключ OCR (OpenAI).'
        };
        enqueueSnackbar(reasonMap[recognition?.reason] || 'Координаты не найдены в тексте или на фото', { variant: 'warning' });
      } else {
        enqueueSnackbar(`Не удалось распознать координаты${recognition?.error ? `: ${recognition.error}` : ''}`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error recognizing coordinates:', error);
      enqueueSnackbar('Ошибка распознавания координат', { variant: 'error' });
    } finally {
      setRecognizingAddressId(null);
    }
  };


  // Отдельный useEffect для фильтров


  const handleAddAddress = () => {
    setEditingAddress(null);
    setFormData(initialFormData);
    setOpenDialog(true);
  };

  const handleEditAddress = async (address) => {
    // Проверяем доступ по лимитам (просмотр + редактирование)
    if (!canViewAddresses || !canEditAddresses) {
      enqueueSnackbar('Недостаточно лимита для просмотра/редактирования', { variant: 'warning' });
      return;
    }

    setEditingAddress(address);
    setFormData({
      ...initialFormData,
      ...address,
      cityId: address.city?._id || '',
      districtId: address.district?._id || '',
      productId: address.product?._id || '',
      packagingId: address.packaging?._id || '',
      courierId: address.courier?._id || ''
    });
    setOpenDialog(true);

    // Фиксируем просмотр адреса (учитывается в суточном лимите просмотра)
    try {
      await axiosInstance.get(`/addresses/${address._id}/view`);
      await refreshUser();
    } catch (error) {
      console.error('Ошибка при учёте просмотра адреса:', error);
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditingAddress(null);
    setFormData(initialFormData);
    setFormError({});
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот адрес?')) return;

    setLoading(true);
    try {
      await axiosInstance.delete(`/addresses/${addressId}`);
      await fetchData();
      await refreshUser();
      enqueueSnackbar('Адрес удален', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting address:', error);
      setFetchError(`Ошибка удаления: ${error.message}`);
      enqueueSnackbar(error.response?.data?.error || 'Не удалось удалить адрес', { variant: 'error' });
      if (error.response?.data?.limitReached) {
        await refreshUser();
      }
    } finally {
      setLoading(false);
    }
  };

  // Массовое удаление адресов
  const handleBulkDelete = async () => {
    if (selectedAddresses.length === 0) {
      enqueueSnackbar('Выберите адреса для удаления', { variant: 'warning' });
      return;
    }

    if (!window.confirm(`Вы уверены, что хотите удалить ${selectedAddresses.length} адрес(ов)?`)) return;

    setLoading(true);
    try {
      await axiosInstance.post('/addresses/bulk-delete', { ids: selectedAddresses });
      setSelectedAddresses([]);
      await fetchData();
      await refreshUser();
      enqueueSnackbar(`Удалено адресов: ${selectedAddresses.length}`, { variant: 'success' });
    } catch (error) {
      console.error('Error bulk deleting addresses:', error);
      setFetchError(`Ошибка массового удаления: ${error.message}`);
      enqueueSnackbar(error.response?.data?.error || 'Не удалось удалить адреса', { variant: 'error' });
      if (error.response?.data?.limitReached) {
        await refreshUser();
      }
    } finally {
      setLoading(false);
    }
  };

  // Массовая остановка адресов
  const handleBulkStop = async () => {
    if (selectedAddresses.length === 0) {
      enqueueSnackbar('Выберите адреса для остановки', { variant: 'warning' });
      return;
    }

    if (!window.confirm(`Вы уверены, что хотите остановить ${selectedAddresses.length} адрес(ов)?`)) return;

    setLoading(true);
    try {
      await axiosInstance.post('/addresses/bulk-stop', { ids: selectedAddresses });
      setSelectedAddresses([]);
      await fetchData();
      enqueueSnackbar(`Остановлено адресов: ${selectedAddresses.length}`, { variant: 'success' });
    } catch (error) {
      console.error('Error bulk stopping addresses:', error);
      setFetchError(`Ошибка массовой остановки: ${error.message}`);
      enqueueSnackbar('Не удалось остановить адреса', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Массовая активация адресов
  const handleBulkActivate = async () => {
    if (selectedAddresses.length === 0) {
      enqueueSnackbar('Выберите адреса для активации', { variant: 'warning' });
      return;
    }

    if (!window.confirm(`Вы уверены, что хотите активировать ${selectedAddresses.length} адрес(ов)?`)) return;

    setLoading(true);
    try {
      await axiosInstance.post('/addresses/bulk-activate', { ids: selectedAddresses });
      setSelectedAddresses([]);
      await fetchData();
      enqueueSnackbar(`Активировано адресов: ${selectedAddresses.length}`, { variant: 'success' });
    } catch (error) {
      console.error('Error bulk activating addresses:', error);
      setFetchError(`Ошибка массовой активации: ${error.message}`);
      enqueueSnackbar('Не удалось активировать адреса', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Открыть диалог установки приоритета
  const openPriorityDialog = () => {
    if (selectedAddresses.length === 0) {
      enqueueSnackbar('Выберите адреса для установки приоритета', { variant: 'warning' });
      return;
    }
    setPriorityValue(0);
    setPriorityDialog(true);
  };

  // Установка приоритета
  const handleSetPriority = async () => {
    if (selectedAddresses.length === 0) return;

    setLoading(true);
    try {
      const response = await axiosInstance.post('/addresses/bulk-priority', { 
        ids: selectedAddresses, 
        priority: parseInt(priorityValue) 
      });
      console.log('Ответ установки приоритета:', response.data);
      console.log('Установлен приоритет:', priorityValue, 'для адресов:', selectedAddresses);
      setSelectedAddresses([]);
      setPriorityDialog(false);
      await fetchData();
      enqueueSnackbar(`Приоритет ${priorityValue} установлен для ${selectedAddresses.length} адрес(ов)`, { variant: 'success' });
    } catch (error) {
      console.error('Error setting priority:', error);
      setFetchError(`Ошибка установки приоритета: ${error.message}`);
      enqueueSnackbar('Не удалось установить приоритет', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Экспорт выделенных адресов в текст
  const handleExportToText = () => {
    if (selectedAddresses.length === 0) {
      enqueueSnackbar('Выберите адреса для экспорта', { variant: 'warning' });
      return;
    }

    // Получаем выбранные адреса
    const selectedData = addresses.filter(addr => selectedAddresses.includes(addr._id));
    
    // Формируем текст
    let textContent = `Экспорт адресов (${selectedData.length} шт.)\n`;
    textContent += `Дата: ${new Date().toLocaleString('ru-RU')}\n`;
    textContent += `${'='.repeat(80)}\n\n`;

    selectedData.forEach((addr, index) => {
      textContent += `${index + 1}. Адрес ID: ${addr._id}\n`;
      textContent += `   Город: ${addr.city?.name || 'Н/Д'}\n`;
      textContent += `   Район: ${addr.district?.name || 'Н/Д'}\n`;
      textContent += `   Товар: ${addr.product?.name || 'Н/Д'}\n`;
      textContent += `   Фасовка: ${addr.packaging?.name || 'Н/Д'}\n`;
      textContent += `   Курьер: ${addr.courier?.name || 'Н/Д'} (${addr.courier?.telegram || ''})\n`;
      textContent += `   Тип: ${TYPES[addr.type] || addr.type}\n`;
      textContent += `   Статус: ${STATUS[addr.status] || addr.status}\n`;
      textContent += `   Приоритет: ${addr.priority || 0}\n`;
      textContent += `   Координаты:\n${addr.photo || 'Н/Д'}\n`;
      textContent += `${'-'.repeat(80)}\n`;
    });

    // Копируем в буфер обмена
    navigator.clipboard.writeText(textContent)
      .then(() => {
        enqueueSnackbar(`Экспортировано ${selectedData.length} адрес(ов) в буфер обмена`, { variant: 'success' });
      })
      .catch((err) => {
        console.error('Ошибка копирования:', err);
        enqueueSnackbar('Не удалось скопировать в буфер обмена', { variant: 'error' });
      });
  };

  // Выбор/снятие выбора всех адресов
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedAddresses(addresses.map(addr => addr._id));
    } else {
      setSelectedAddresses([]);
    }
  };

  // Выбор/снятие выбора одного адреса
  const handleSelectAddress = (addressId) => {
    setSelectedAddresses(prev => {
      if (prev.includes(addressId)) {
        return prev.filter(id => id !== addressId);
      } else {
        return [...prev, addressId];
      }
    });
  };

  // Функция для выдачи адреса
  const handleIssueAddress = async () => {
    if (!issuingAddress) return;

    setLoading(true);
    try {
      const response = await axiosInstance.put(`/addresses/${issuingAddress._id}/issue`, {
        comment: issueComment
      });
      
      // Обновляем адрес в списке
      setAddresses(addresses.map(addr => 
        addr._id === issuingAddress._id ? response.data.data : addr
      ));
      
      setIssueDialog(false);
      setIssueComment('');
      setIssuingAddress(null);
    } catch (error) {
      console.error('Error issuing address:', error);
      setFetchError(`Ошибка выдачи адреса: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Открыть диалог выдачи адреса
  const openIssueDialog = (address) => {
    setIssuingAddress(address);
    setIssueComment('');
    setIssueDialog(true);
  };

  // Открыть диалог просмотра комментария
  const openCommentDialog = (address) => {
    setViewingAddress(address);
    setCommentDialog(true);
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'cityId') {
        // Если меняется город, сбрасываем район
        console.log('Город изменен на:', value);
        console.log('Доступные районы:', districts);
        console.log('Отфильтрованные районы:', districts.filter(d => d.city?._id === value));
        return {
          ...prev,
          cityId: value,
          districtId: ''
        };
      } else if (name === 'productId') {
        // Если меняется товар, сбрасываем фасовку
        console.log('Товар изменен на:', value);
        console.log('Доступные фасовки:', packagings);
        console.log('Отфильтрованные фасовки:', packagings.filter(p => p.product?._id === value));
        return {
          ...prev,
          productId: value,
          packagingId: ''
        };
      }
      return {
        ...prev,
        [name]: value
      };
    });
    if (formError[name]) {
      setFormError(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    console.log('Начинаем валидацию формы');
    console.log('Текущие данные формы:', formData);
    
    const errors = {};
    const required = ['cityId', 'districtId', 'productId', 'packagingId', 'courierId', 'type', 'photo'];
    
    required.forEach(field => {
      if (!formData[field]) {
        console.log(`Ошибка: поле ${field} пустое`);
        errors[field] = 'Обязательное поле';
      }
    });



    console.log('Найденные ошибки:', errors);
    setFormError(errors);
    const isValid = Object.keys(errors).length === 0;
    console.log('Форма валидна?', isValid);
    return isValid;
  };

  const handleSubmit = async () => {
    console.log('Нажата кнопка Сохранить');
    
    if (!validateForm()) {
      console.log('Валидация не прошла');
      return;
    }

    if (submitting) {
      console.log('Запрос уже выполняется, игнорируем повторное нажатие');
      return;
    }

    setSubmitting(true);
    setLoading(true);
    try {
      // Преобразуем данные формы в формат для API
      const apiData = {
        city: formData.cityId,
        district: formData.districtId,
        product: formData.productId,
        packaging: formData.packagingId,
        courier: formData.courierId,
        type: formData.type,
        status: formData.status,
        photo: formData.photo,
      };

      console.log('Отправляем данные на сервер:', apiData);

      if (editingAddress) {
        console.log('Редактируем существующий адрес:', editingAddress._id);
        const response = await axiosInstance.put(`/addresses/${editingAddress._id}`, apiData);
        console.log('Ответ сервера:', response.data);
      } else {
        console.log('Создаем новый адрес');
        const response = await axiosInstance.post('/addresses', {
          ...apiData,
          recognizeCoordinates: formData.recognizeCoordinates
        });
        console.log('Ответ сервера:', response.data);
      }
      
      console.log('Успешно сохранено, обновляем данные');
      enqueueSnackbar(editingAddress ? 'Адрес успешно обновлен' : 'Адрес успешно создан', { variant: 'success' });
      fetchData();
      if (editingAddress) {
        await refreshUser();
      }
      handleClose();
    } catch (error) {
      console.error('Ошибка при сохранении:', error);
      console.error('Детали ошибки:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Ошибка сохранения';
      setFormError(prev => ({
        ...prev,
        submit: errorMessage
      }));
      enqueueSnackbar(errorMessage, { variant: 'error' });
      if (error.response?.data?.limitReached) {
        await refreshUser();
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Фильтрация адресов

  // Обработчик изменения фильтров
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    console.log('Изменение фильтра:', name, value);
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [name]: value
      };
      console.log('Новые фильтры:', newFilters);
      return newFilters;
    });

    // Сбрасываем страницу при изменении фильтров
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };
useEffect(() => {
  fetchData();
  setSelectedAddresses([]); // Сбрасываем выбор при изменении фильтров или страницы
}, [filters, pagination.page, pagination.limit]);

useEffect(() => {
  fetchOpenAiOcrBalance();
}, [fetchOpenAiOcrBalance]);
  // Обработчик изменения страницы
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage + 1
    }));
  };

  // Обработчик изменения количества элементов на странице
  const handleLimitChange = (event) => {
    setPagination(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 1
    }));
  };

  return (
    <Fade in={true} timeout={800}>
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {/* Hero Section */}
        <Paper 
          elevation={4}
          sx={{ 
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: 3,
            mb: 3,
            color: 'text.primary'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 56, height: 56 }}>
                <IssueIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Управление адресами
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Формирование адресов, фильтрация и выдача курьерам
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Обновить данные">
                <IconButton 
                  onClick={handleRefreshPageData}
                  disabled={loading}
                  sx={{ 
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  {loading ? <LinearProgress sx={{ width: 70, color: 'white' }} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
              <Button 
                variant="contained"
                onClick={handleBulkStop}
                startIcon={<StopIcon />}
                disabled={selectedAddresses.length === 0 || loading}
                sx={{
                  bgcolor: 'action.selected',
                  color: 'warning.main',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:disabled': { opacity: 0.5 }
                }}
              >
                Стоп ({selectedAddresses.length})
              </Button>
              <Button 
                variant="contained"
                onClick={handleBulkActivate}
                startIcon={<ActivateIcon />}
                disabled={selectedAddresses.length === 0 || loading}
                sx={{
                  bgcolor: 'action.selected',
                  color: 'success.main',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:disabled': { opacity: 0.5 }
                }}
              >
                Активировать ({selectedAddresses.length})
              </Button>
              <Button 
                variant="contained"
                onClick={openPriorityDialog}
                startIcon={<PriorityIcon />}
                disabled={selectedAddresses.length === 0 || loading}
                sx={{
                  bgcolor: 'action.selected',
                  color: 'secondary.main',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:disabled': { opacity: 0.5 }
                }}
              >
                Приоритет ({selectedAddresses.length})
              </Button>
              <Button 
                variant="contained"
                onClick={handleExportToText}
                startIcon={<CopyIcon />}
                disabled={selectedAddresses.length === 0 || loading}
                sx={{
                  background: selectedAddresses.length > 0 
                    ? `linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)` 
                    : 'rgba(255,255,255,0.1)',
                  color: 'white',
                  '&:hover': {
                    background: selectedAddresses.length > 0
                      ? `linear-gradient(135deg, #1976d2 0%, #0288d1 100%)`
                      : 'rgba(255,255,255,0.1)'
                  },
                  '&:disabled': { opacity: 0.5 }
                }}
              >
                Экспорт ({selectedAddresses.length})
              </Button>
              <Button 
                variant="contained"
                onClick={handleBulkDelete}
                startIcon={<DeleteSweepIcon />}
                disabled={selectedAddresses.length === 0 || loading}
                sx={{
                  background: selectedAddresses.length > 0 
                    ? `linear-gradient(135deg, #f44336 0%, #e91e63 100%)` 
                    : 'rgba(255,255,255,0.1)',
                  color: 'white',
                  '&:hover': {
                    background: selectedAddresses.length > 0
                      ? `linear-gradient(135deg, #d32f2f 0%, #c2185b 100%)`
                      : 'rgba(255,255,255,0.1)'
                  },
                  '&:disabled': { opacity: 0.5 }
                }}
              >
                Удалить ({selectedAddresses.length})
              </Button>
              <Button 
                variant="contained"
                onClick={handleAddAddress}
                startIcon={<AddIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`,
                  color: 'white',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                  }
                }}
              >
                Добавить адрес
              </Button>
            </Stack>
          </Box>
        </Paper>

        {/* Фильтры */}
        <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              Фильтры
            </Typography>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Город</InputLabel>
          <Select
            name="cityId"
            value={filters.cityId}
            onChange={handleFilterChange}
            label="Город"
          >
            <MenuItem value="">
              <em>Все</em>
            </MenuItem>
            {cities.map(city => (
              <MenuItem key={city._id} value={city._id}>
                {city.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Район</InputLabel>
          <Select
            name="districtId"
            value={filters.districtId}
            onChange={handleFilterChange}
            label="Район"
            disabled={!filters.cityId}
          >
            <MenuItem value="">
              <em>Все</em>
            </MenuItem>
            {districts
              .filter(d => !filters.cityId || d.city?._id === filters.cityId)
              .map(district => (
                <MenuItem key={district._id} value={district._id}>
                  {district.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Тип</InputLabel>
          <Select
            name="type"
            value={filters.type}
            onChange={handleFilterChange}
            label="Тип"
          >
            <MenuItem value="">
              <em>Все</em>
            </MenuItem>
            {Object.entries(TYPES).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Статус</InputLabel>
          <Select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            label="Статус"
          >
            <MenuItem value="">
              <em>Все</em>
            </MenuItem>
            {Object.entries(STATUS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Курьер</InputLabel>
          <Select
            name="courierId"
            value={filters.courierId}
            onChange={handleFilterChange}
            label="Курьер"
          >
            <MenuItem value="">
              <em>Все</em>
            </MenuItem>
            {couriers.map(courier => (
              <MenuItem key={courier._id} value={courier._id}>
                {courier.name || courier.username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Фасовка</InputLabel>
          <Select
            name="packagingId"
            value={filters.packagingId}
            onChange={handleFilterChange}
            label="Фасовка"
          >
            <MenuItem value="">
              <em>Все</em>
            </MenuItem>
            {packagings.map(packaging => (
              <MenuItem key={packaging._id} value={packaging._id}>
                {packaging.product?.name ? `${packaging.product.name} — ${packaging.name}` : packaging.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Фото</InputLabel>
          <TextField
            name="photo"
            value={filters.photo}
            onChange={handleFilterChange}
            label="Фото"
          />
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>_id</InputLabel>
          <TextField
            name="id"
            value={filters.id}
            onChange={handleFilterChange}
            label="ID"
            placeholder="ID"
          />
        </FormControl>
            </Box>
          </Box>
        </Paper>

        {/* Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {loading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Skeleton variant="text" width={120} height={24} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={80} height={36} />
                </Card>
              </Grid>
            ))
          ) : (
            <>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="body2" color="text.secondary">Всего адресов</Typography>
                  <Typography variant="h4" fontWeight="bold">{addresses.length}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="body2" color="text.secondary">Активные</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">{addresses.filter(a => a.status === 'active').length}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="body2" color="text.secondary">Остановлено</Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">{addresses.filter(a => a.status === 'stopped').length}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="body2" color="text.secondary">Выдано</Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">{addresses.filter(a => a.status === 'issued').length}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="body2" color="text.secondary">Неактивные</Typography>
                  <Typography variant="h4" fontWeight="bold" color="text.secondary">{addresses.filter(a => a.status === 'inactive').length}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">OCR баланс OpenAI</Typography>
                    <IconButton size="small" onClick={fetchOpenAiOcrBalance} disabled={ocrBalanceLoading}>
                      {ocrBalanceLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </Box>

                  {ocrBalance?.ok ? (
                    <>
                      <Typography variant="h4" fontWeight="bold" color="primary.main">
                        {formatUsd(ocrBalance.totalAvailable)} {ocrBalance.currency || 'USD'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Использовано: {formatUsd(ocrBalance.totalUsed)} / Начислено: {formatUsd(ocrBalance.totalGranted)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6" fontWeight="bold" color="warning.main">Недоступно</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ocrBalance?.error || ocrBalance?.reason || 'Не удалось получить баланс'}
                        {ocrBalance?.status ? ` (HTTP ${ocrBalance.status})` : ''}
                      </Typography>
                    </>
                  )}
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        {/* Таблица адресов */}
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">Список адресов ({loading ? '...' : addresses.length})</Typography>
            {!loading && addresses.length === 0 && (
              <Chip label="Нет результатов" color="warning" variant="outlined" />
            )}
          </Box>
          {loading && <LinearProgress />}
          <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedAddresses.length > 0 && selectedAddresses.length < addresses.length}
                  checked={addresses.length > 0 && selectedAddresses.length === addresses.length}
                  onChange={handleSelectAll}
                  disabled={addresses.length === 0}
                />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Приоритет</TableCell>
              <TableCell>Город</TableCell>
              <TableCell>Район</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Товар</TableCell>
              <TableCell>Фасовка</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Координаты</TableCell>
              <TableCell>Курьер</TableCell>
              <TableCell>Добавлен</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {addresses.map((address) => (
              <TableRow 
                key={address._id}
                selected={selectedAddresses.includes(address._id)}
                hover
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedAddresses.includes(address._id)}
                    onChange={() => handleSelectAddress(address._id)}
                  />
                </TableCell>
                <TableCell>{address._id}</TableCell>
                <TableCell>
                  <Chip 
                    label={address.priority || 0} 
                    size="small"
                    color={address.priority > 50 ? 'error' : address.priority > 0 ? 'warning' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{address.city?.name}</TableCell>
                <TableCell>{address.district?.name}</TableCell>
                <TableCell>{TYPES[address.type]}</TableCell>
                <TableCell>{address.product?.name}</TableCell>
                <TableCell>{address.packaging?.name}</TableCell>
                <TableCell>
                  <Chip 
                    label={STATUS[address.status]} 
                    size="small"
                    color={
                      address.status === 'active' ? 'success' :
                      address.status === 'stopped' ? 'warning' :
                      address.status === 'issued' ? 'info' :
                      'default'
                    }
                  />
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={COORD_STATUS_LABEL[address.coordinateRecognition?.status] || 'Нет данных'}
                      color={getCoordStatusColor(address.coordinateRecognition?.status)}
                    />
                    {address.coordinateRecognition?.status === 'success' && formatCoordinateValue(address.coordinateRecognition?.latitude) && formatCoordinateValue(address.coordinateRecognition?.longitude) && (
                      <Typography variant="caption" color="text.secondary">
                        {`${formatCoordinateValue(address.coordinateRecognition?.latitude)}, ${formatCoordinateValue(address.coordinateRecognition?.longitude)}`}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
             
                <TableCell>{address.courier?.name} ({address.courier?.username})</TableCell>
                <TableCell>{formatDate(address.createdAt)}</TableCell>
                <TableCell>
                  <Tooltip title="Распознать координаты с фото">
                    <span>
                      <IconButton
                        onClick={() => handleRecognizeCoordinates(address)}
                        color="secondary"
                        size="small"
                        disabled={recognizingAddressId === address._id}
                      >
                        {recognizingAddressId === address._id ? <CircularProgress size={18} /> : <RecognizeCoordsIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={
                    !canViewAddresses ? 'Исчерпан суточный лимит на просмотр' :
                    !canEditAddresses ? 'Исчерпан суточный лимит на редактирование' :
                    (viewLeft != null ? `Осталось просмотров: ${viewLeft}` : 'Редактировать')
                  }>
                    <span>
                      <IconButton onClick={() => handleEditAddress(address)} color="primary" size="small" disabled={!canViewAddresses || !canEditAddresses}>
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {address.status !== 'issued' && (
                    <IconButton onClick={() => openIssueDialog(address)} color="success" size="small">
                      <IssueIcon />
                    </IconButton>
                  )}
                  {address.status === 'issued' && address.issueComment && (
                    <IconButton onClick={() => openCommentDialog(address)} color="info" size="small">
                      <CommentIcon />
                    </IconButton>
                  )}
                  <Tooltip title={!canDeleteAddresses ? 'Исчерпан суточный лимит на удаление' : (deleteLeft != null ? `Осталось удалений: ${deleteLeft}` : 'Удалить')}>
                    <span>
                      <IconButton onClick={() => handleDeleteAddress(address._id)} color="error" size="small" disabled={!canDeleteAddresses}>
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
          </TableContainer>

        </Paper>

        {/* Пагинация */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 120 }}>
          <Select
            value={pagination.limit}
            onChange={handleLimitChange}
            size="small"
          >
            {[5, 10, 25, 50].map(pageSize => (
              <MenuItem key={pageSize} value={pageSize}>
                {pageSize} на странице
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
        />
      </Box>

      {/* Диалог добавления/редактирования */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAddress ? 'Редактировать адрес' : 'Добавить адрес'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Город</InputLabel>
              <Select
                name="cityId"
                value={formData.cityId}
                onChange={handleInputChange}
                label="Город"
              >
                {cities.map(city => (
                  <MenuItem key={city._id} value={city._id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Район</InputLabel>
              <Select
                name="districtId"
                value={formData.cityId ? formData.districtId : ''}
                onChange={handleInputChange}
                label="Район"
                disabled={!formData.cityId}
              >
                {districts
                  .filter(d => d.city?._id === formData.cityId)
                  .map(district => (
                    <MenuItem key={district._id} value={district._id}>
                      {district.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Тип</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                label="Тип"
              >
                {Object.entries(TYPES).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Товар</InputLabel>
              <Select
                name="productId"
                value={formData.productId}
                onChange={handleInputChange}
                label="Товар"
              >
                {products.map(product => (
                  <MenuItem key={product._id} value={product._id}>
                    {product.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Фасовка</InputLabel>
              <Select
                name="packagingId"
                value={formData.productId ? formData.packagingId : ''}
                onChange={handleInputChange}
                label="Фасовка"
                disabled={!formData.productId}
              >
                {packagings
                  .filter(p => p.product?._id === formData.productId)
                  .map(packaging => (
                    <MenuItem key={packaging._id} value={packaging._id}>
                      {packaging.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Курьер</InputLabel>
              <Select
                name="courierId"
                value={formData.courierId}
                onChange={handleInputChange}
                label="Курьер"
              >
                {couriers.map(courier => (
                  <MenuItem key={courier._id} value={courier._id}>
                    {courier.name || courier.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Фото"
              name="photo"
              value={formData.photo}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              multiline
              minRows={4}
            />
            {!editingAddress && (
              <FormControlLabel
                control={
                  <Checkbox
                    name="recognizeCoordinates"
                    checked={formData.recognizeCoordinates}
                    onChange={(_, checked) => setFormData(prev => ({ ...prev, recognizeCoordinates: checked }))}
                    disabled={submitting}
                  />
                }
                label="Распознать координаты"
              />
            )}
          </Box>
          <Box sx={{ mb: 1, pl: 2, pr: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box>
              Будет добавлено <b>{addressCount}</b> адресов
            </Box>
            <Box>
              Одна строка - 1 адрес
            </Box>
          </Typography>
        </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} color="inherit" disabled={submitting}>
            Отмена
          </Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submitting ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог выдачи адреса */}
      <Dialog open={issueDialog} onClose={() => setIssueDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Выдать адрес</DialogTitle>
        <DialogContent>
          {issuingAddress && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Адрес: {issuingAddress.city?.name}, {issuingAddress.district?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Товар: {issuingAddress.product?.name} - {issuingAddress.packaging?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Курьер: {issuingAddress.courier?.name} ({issuingAddress.courier?.username})
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Комментарий (необязательно)"
            value={issueComment}
            onChange={(e) => setIssueComment(e.target.value)}
            placeholder="Введите комментарий к выдаче адреса..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueDialog(false)}>Отмена</Button>
          <Button 
            onClick={handleIssueAddress} 
            variant="contained" 
            color="success"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Выдать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог просмотра комментария */}
      <Dialog open={commentDialog} onClose={() => setCommentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Комментарий к выдаче</DialogTitle>
        <DialogContent>
          {viewingAddress && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Адрес: {viewingAddress.city?.name}, {viewingAddress.district?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Выдан: {formatDate(viewingAddress.issuedAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Выдал: {viewingAddress.issuedBy?.name || 'Неизвестно'}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Комментарий"
                value={viewingAddress.issueComment || 'Комментарий отсутствует'}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialog(false)}>Закрыть</Button>
        </DialogActions>
        </Dialog>

      {/* Диалог установки приоритета */}
      <Dialog open={priorityDialog} onClose={() => setPriorityDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Установить приоритет продажи</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Выбрано адресов: <strong>{selectedAddresses.length}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Приоритет определяет очередность продажи адресов в боте. Чем выше значение (0-100), тем раньше адрес будет продан.
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Приоритет (0-100)"
              value={priorityValue}
              onChange={(e) => {
                const value = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                setPriorityValue(value);
              }}
              inputProps={{ min: 0, max: 100 }}
              helperText="0 = обычный приоритет, 100 = максимальный приоритет"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriorityDialog(false)} color="inherit">
            Отмена
          </Button>
          <Button onClick={handleSetPriority} color="primary" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Установить'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Fade>
  );
};

export default Addresses;